/**
 * Country Modal
 *
 * Modal for creating and editing countries.
 *
 * Features:
 *   - Preset catalog selector (ISO 3166 LATAM + mundiales): un click
 *     llena code + name + officialName + phoneCode + flag + sugerencias
 *     de moneda/idioma.
 *   - Auto-derivación de la flag emoji cuando el admin tipea el código
 *     ISO manualmente (si el campo flag está vacío).
 *   - Guard bloqueante cuando no hay monedas o idiomas activos en la
 *     plataforma: muestra CTAs a /currencies y /languages en lugar del
 *     formulario.
 *   - En edición, el campo `isActive` NO se puede alternar libremente:
 *     la activación siempre pasa por el flujo con requirements check.
 *     Solo se puede desactivar desde aquí (toggle a false).
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { Button, Input, Select, Modal } from '@/components/ui';
import { countryService } from '@/services/country.service';
import type {
  Country,
  CreateCountryDto,
  UpdateCountryDto,
} from '@/types/country.types';
import type { Currency } from '@/types/currency.types';
import type { Language } from '@/types/language.types';
import { COUNTRY_CATALOG } from './country-catalog';
import { codeToFlag } from './codeToFlag';

interface CountryModalProps {
  isOpen: boolean;
  country: Country | null;
  currencies: Currency[];
  languages: Language[];
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Timezone por defecto de un mercado nuevo. Coincide con el default de la
 * columna y con `stores.timezone`: hoy sólo opera Colombia, y un mercado
 * distinto debe cambiarla al crearse — de ella depende cuándo empieza "hoy"
 * en los paneles operativos.
 */
const DEFAULT_TIMEZONE = 'America/Bogota';

export function CountryModal({
  isOpen,
  country,
  currencies,
  languages,
  onClose,
  onSuccess,
}: CountryModalProps) {
  const isEdit = !!country;
  const navigate = useNavigate();

  const setupReady = currencies.length > 0 && languages.length > 0;

  // Form state
  const [catalogCode, setCatalogCode] = useState(''); // preset selector
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [officialName, setOfficialName] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [flagEmoji, setFlagEmoji] = useState('');
  const [currencyId, setCurrencyId] = useState('');
  const [languageId, setLanguageId] = useState('');
  // Radio (m) para geo-fence de pedidos TABLE. Vacío = sin validación
  // (envíamos null al backend). Default 100 al crear.
  const [tableRadiusMeters, setTableRadiusMeters] = useState<string>('100');
  const [timezone, setTimezone] = useState<string>(DEFAULT_TIMEZONE);
  const [isLoading, setIsLoading] = useState(false);

  // Field errors state
  const [errors, setErrors] = useState<{
    code?: string;
    name?: string;
    phoneCode?: string;
    currencyId?: string;
    languageId?: string;
    timezone?: string;
  }>({});

  // Filtra el catálogo: esconde los países que ya están creados (evita
  // que el admin elija un duplicado y choque con el 409 del backend).
  // Si estás editando, no aplica — el modal no muestra el selector.
  // No tenemos la lista completa aquí, pero con catalogCode que se
  // resetea al abrir, es suficiente para la UX primer-uso.
  const catalogOptions = useMemo(
    () =>
      COUNTRY_CATALOG.map((c) => ({
        value: c.code,
        label: `${c.flagEmoji} ${c.name} (${c.code})`,
      })),
    [],
  );

  // Reset form when modal opens/closes or country changes
  useEffect(() => {
    if (isOpen) {
      if (country) {
        setCatalogCode('');
        setCode(country.code);
        setName(country.name);
        setOfficialName(country.officialName || '');
        setPhoneCode(country.phoneCode);
        setFlagEmoji(country.flagEmoji || '');
        setCurrencyId(country.currency.id);
        setLanguageId(country.language.id);
        setTableRadiusMeters(
          country.tableOrderRadiusMeters == null
            ? ''
            : String(country.tableOrderRadiusMeters),
        );
        setTimezone(country.timezone || DEFAULT_TIMEZONE);
        setErrors({});
      } else {
        setCatalogCode('');
        setCode('');
        setName('');
        setOfficialName('');
        setPhoneCode('');
        setFlagEmoji('');
        setTimezone(DEFAULT_TIMEZONE);
        setCurrencyId(currencies[0]?.id || '');
        setLanguageId(languages[0]?.id || '');
        setTableRadiusMeters('100');
        setErrors({});
      }
    }
  }, [isOpen, country, currencies, languages]);

  /**
   * Al elegir un país del catálogo, autocompleta todos los campos
   * textuales y resuelve las sugerencias de moneda/idioma contra los
   * catálogos actuales (solo si existen).
   */
  const handlePickFromCatalog = (code2: string) => {
    setCatalogCode(code2);
    if (!code2) return;

    const entry = COUNTRY_CATALOG.find((c) => c.code === code2);
    if (!entry) return;

    setCode(entry.code);
    setName(entry.name);
    setOfficialName(entry.officialName ?? '');
    setPhoneCode(entry.phoneCode);
    setFlagEmoji(entry.flagEmoji);

    // Resolver sugerencias — si no están en el platform, dejar el pick
    // manual que ya había.
    if (entry.suggestedCurrency) {
      const match = currencies.find(
        (c) => c.code.toUpperCase() === entry.suggestedCurrency?.toUpperCase(),
      );
      if (match) setCurrencyId(match.id);
    }
    if (entry.suggestedLanguage) {
      const match = languages.find(
        (l) => l.code.toLowerCase() === entry.suggestedLanguage?.toLowerCase(),
      );
      if (match) setLanguageId(match.id);
    }

    setErrors({});
  };

  /**
   * Handler del campo Code. Auto-genera la flag emoji si:
   *   - el admin todavía no la escribió a mano, o
   *   - la flag actual corresponde a otro código (seguía mostrando CO
   *     pero cambió a MX).
   */
  const handleCodeChange = (raw: string) => {
    const next = raw.toUpperCase();
    setCode(next);
    if (errors.code) setErrors((prev) => ({ ...prev, code: undefined }));

    const derived = codeToFlag(next);
    // Re-sincroniza la bandera con el nuevo código si la actual está
    // vacía o es la que derivaríamos del código anterior.
    if (!flagEmoji || flagEmoji === codeToFlag(code)) {
      setFlagEmoji(derived);
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!code.trim()) {
      newErrors.code = 'El código es requerido';
    } else if (code.trim().length !== 2) {
      newErrors.code = 'El código debe tener exactamente 2 caracteres';
    }

    if (!name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }

    if (!phoneCode.trim()) {
      newErrors.phoneCode = 'El código telefónico es requerido';
    } else if (!phoneCode.trim().match(/^\+\d{1,4}$/)) {
      newErrors.phoneCode = 'Debe empezar con + seguido de dígitos (ej: +57)';
    }

    if (!currencyId) {
      newErrors.currencyId = 'La moneda es requerida';
    }
    if (timezone.trim() && !/^[A-Za-z]+\/[A-Za-z_+\-0-9]+(\/[A-Za-z_+\-0-9]+)?$/.test(timezone.trim())) {
      newErrors.timezone = 'Formato IANA, por ejemplo America/Bogota';
    }

    if (!languageId) {
      newErrors.languageId = 'El idioma es requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      if (isEdit && country) {
        const updateData: UpdateCountryDto = {
          name: name.trim(),
          officialName: officialName.trim() || undefined,
          phoneCode: phoneCode.trim(),
          flagEmoji: flagEmoji.trim() || undefined,
          currencyId,
          languageId,
          tableOrderRadiusMeters:
            tableRadiusMeters.trim() === ''
              ? null
              : Number(tableRadiusMeters),
          timezone: timezone.trim() || undefined,
        };
        await countryService.update(country.id, updateData);
        toast.success('País actualizado');
      } else {
        const createData: CreateCountryDto = {
          code: code.trim().toUpperCase(),
          name: name.trim(),
          officialName: officialName.trim() || undefined,
          phoneCode: phoneCode.trim(),
          flagEmoji: flagEmoji.trim() || undefined,
          currencyId,
          languageId,
          // Backend ignora este campo y fuerza inactive. Lo enviamos
          // explícito solo por transparencia.
          isActive: false,
        };
        await countryService.create(createData);
        toast.success('País creado. Actívalo desde el listado tras configurar los prerequisitos.');
      }
      onSuccess();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error al guardar');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Guard: no hay moneda o idioma activos ───────────

  if (!setupReady && !isEdit) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Antes de crear países"
        size="md"
        footer={
          <Button variant="outlined" onClick={onClose}>
            Cerrar
          </Button>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Para crear un país necesitas tener al menos una moneda y un idioma
            registrados en la plataforma.
          </p>

          <div className="space-y-2">
            <SetupRow
              done={currencies.length > 0}
              label="Moneda registrada"
              actionLabel="Ir a monedas"
              onAction={() => {
                onClose();
                navigate('/currencies');
              }}
            />
            <SetupRow
              done={languages.length > 0}
              label="Idioma registrado"
              actionLabel="Ir a idiomas"
              onAction={() => {
                onClose();
                navigate('/languages');
              }}
            />
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Editar País' : 'Nuevo País'}
      size="lg"
      footer={
        <>
          <Button type="button" variant="outlined" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            form="country-form"
            disabled={isLoading}
            isLoading={isLoading}
          >
            {isEdit ? 'Guardar Cambios' : 'Crear País'}
          </Button>
        </>
      }
    >
      <form id="country-form" onSubmit={handleSubmit} className="space-y-4">
        {/* Preset catalog — solo en creación */}
        {!isEdit && (
          <div className="rounded-lg border border-border bg-background-secondary/40 p-3 space-y-2">
            <label className="block text-xs font-medium text-foreground-secondary">
              Cargar desde catálogo (opcional)
            </label>
            <Select
              value={catalogCode}
              onChange={(e) => handlePickFromCatalog(e.target.value)}
              placeholder="Selecciona un país preconfigurado..."
              options={catalogOptions}
            />
            <p className="text-[11px] text-muted-foreground">
              Rellena código, nombre, teléfono y bandera. Puedes editarlos después.
            </p>
          </div>
        )}

        {/* Code — solo en creación */}
        {!isEdit && (
          <Input
            label="Código ISO (2 letras)"
            required
            value={code}
            onChange={(e) => handleCodeChange(e.target.value)}
            maxLength={2}
            placeholder="CO"
            className="font-mono uppercase"
            error={errors.code}
          />
        )}

        <Input
          label="Nombre"
          required
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
          }}
          maxLength={100}
          placeholder="Colombia"
          error={errors.name}
        />

        <Input
          label="Nombre Oficial"
          value={officialName}
          onChange={(e) => setOfficialName(e.target.value)}
          maxLength={150}
          placeholder="República de Colombia"
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Código Telefónico"
            required
            value={phoneCode}
            onChange={(e) => {
              setPhoneCode(e.target.value);
              if (errors.phoneCode)
                setErrors((prev) => ({ ...prev, phoneCode: undefined }));
            }}
            maxLength={5}
            placeholder="+57"
            error={errors.phoneCode}
          />

          <Input
            label="Emoji Bandera"
            value={flagEmoji}
            onChange={(e) => setFlagEmoji(e.target.value)}
            maxLength={10}
            placeholder="🇨🇴"
          />
        </div>

        {/* La moneda del país DENOMINA todos los importes del mercado:
            `Wallet` no guarda moneda propia, la resuelve por el país. Cambiarla
            no convierte los saldos, los reinterpreta — 50.000 COP pasarían a
            leerse como 50.000 USD. Por eso queda bloqueada en un país activo, y
            el backend la rechaza además si ya hay billeteras, compañías o
            clientes. */}
        <Select
          label="Moneda"
          required
          disabled={isEdit && !!country?.isActive}
          value={currencyId}
          onChange={(e) => {
            setCurrencyId(e.target.value);
            if (errors.currencyId)
              setErrors((prev) => ({ ...prev, currencyId: undefined }));
          }}
          placeholder="Seleccionar moneda..."
          options={currencies.map((c) => ({
            value: c.id,
            label: `${c.symbol} ${c.code} - ${c.name}`,
          }))}
          error={errors.currencyId}
          helperText={
            isEdit
              ? country?.isActive
                ? 'No se puede cambiar en un país activo: denomina todos los saldos, precios y liquidaciones del mercado.'
                : 'Sólo se puede cambiar mientras el país no tenga billeteras, compañías ni clientes.'
              : undefined
          }
        />

        <Select
          label="Idioma"
          required
          value={languageId}
          onChange={(e) => {
            setLanguageId(e.target.value);
            if (errors.languageId)
              setErrors((prev) => ({ ...prev, languageId: undefined }));
          }}
          placeholder="Seleccionar idioma..."
          options={languages.map((l) => ({
            value: l.id,
            label: `${l.code} - ${l.nativeName}`,
          }))}
          error={errors.languageId}
        />

        {/* Geo-fence para pedidos a la mesa. Vac\u00edo = sin validaci\u00f3n
            (\u00fatil para mercados sin GPS confiable o sin requirement). */}
        <Input
          type="number"
          min={0}
          max={5000}
          label="Radio para pedidos a la mesa (metros)"
          value={tableRadiusMeters}
          onChange={(e) => setTableRadiusMeters(e.target.value)}
          placeholder="100"
          helperText="Los clientes deben estar a esta distancia o menos de la sucursal para crear pedidos TABLE. D\u00e9jalo vac\u00edo para desactivar la validaci\u00f3n en este pa\u00eds."
        />

        <Input
          label="Zona horaria (IANA)"
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          placeholder={DEFAULT_TIMEZONE}
          error={errors.timezone}
          helperText='Decide cuándo empieza "hoy" en los paneles operativos de este país. Formato IANA, por ejemplo America/Bogota o America/Mexico_City.'
        />

        {/* Apagar un país no se hace desde aquí.
            Este interruptor lo desactivaba en silencio, al guardar, mezclado
            con un cambio de nombre o de zona horaria. Detiene la operación
            completa del país y exige el código del correo y el del
            autenticador, así que vive en su propia acción — donde se puede
            explicar qué va a pasar antes de que pase. */}
        {isEdit && country?.isActive ? (
          <div className="rounded-lg border border-warning/30 bg-warning-subtle p-3">
            <p className="text-sm font-medium">El país está activo</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Para desactivarlo, cierra esto y usa{' '}
              <strong>Desactivar País</strong> en el menú de acciones. Detiene
              su operación completa, así que pide confirmación por correo y el
              código de tu autenticador.
            </p>
          </div>
        ) : !isEdit ? (
          <div className="rounded-lg border border-warning/30 bg-warning-subtle p-3">
            <div className="flex items-start gap-2">
              <InfoIcon />
              <div>
                <p className="text-sm font-medium text-foreground">
                  El país se creará inactivo
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Luego configura provincias, ciudades, impuestos, tarifas de
                  servicio y payment gateways. Ahí podrás activarlo desde el
                  listado.
                </p>
              </div>
            </div>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}

// ── Guard setup row ───────────────────────────────────

interface SetupRowProps {
  done: boolean;
  label: string;
  actionLabel: string;
  onAction: () => void;
}

function SetupRow({ done, label, actionLabel, onAction }: SetupRowProps) {
  return (
    <div
      className={`flex items-center justify-between rounded-lg border p-3 ${done ? 'border-success/30 bg-success-subtle' : 'border-warning/30 bg-warning-subtle'}`}
    >
      <div className="flex items-center gap-2">
        {done ? (
          <svg className="h-4 w-4 text-success" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          <span className="inline-block h-2 w-2 rounded-full bg-warning" />
        )}
        <span className="text-sm font-medium">{label}</span>
      </div>
      {!done && (
        <button
          type="button"
          onClick={onAction}
          className="text-xs font-medium text-primary hover:underline underline-offset-2"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

function InfoIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-5 w-5 text-warning flex-shrink-0 mt-0.5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}
